<template>
  <v-card-text>
    <v-row>
      <v-col
        cols="12"
        md="9"
      >
        <div class="d-flex align-center mb-2">
          <v-btn
            small
            text
            @click="$refs.calendar.prev()"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            small
            text
            @click="$refs.calendar.next()"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <span class="text-subtitle-1 ml-2">{{ title }}</span>
          <v-spacer />
          <span class="text-caption grey--text mr-4 d-none d-sm-flex align-center">
            <v-icon
              x-small
              class="mr-1"
            >
              mdi-account-group
            </v-icon>
            {{ audienceFilter ? 'Goes to everyone' : 'Emails everyone' }}
          </span>
          <v-btn-toggle
            v-model="type"
            color="mcsprimary"
            dense
            mandatory
          >
            <v-btn
              small
              value="month"
            >
              Month
            </v-btn>
            <v-btn
              small
              value="week"
            >
              Week
            </v-btn>
          </v-btn-toggle>
        </div>
        <!-- Filtered, the calendar deliberately shows more than this audience's own campaigns, so say so. -->
        <div
          v-if="audienceFilter"
          class="text-caption grey--text mb-2"
        >
          Showing campaigns for {{ audienceName || 'this audience' }}, plus emails and social posts that go to
          everyone.
        </div>
        <v-calendar
          ref="calendar"
          v-model="focus"
          :type="type"
          :events="calendarEvents"
          :event-color="eventColor"
          @click:event="openEvent"
          @change="updateTitle"
        >
          <!-- Month view: render campaign chips directly in the day cell (avoids v-calendar's
               month-view event overflow that collapses everything to "N more"). -->
          <template #day="{ date }">
            <div class="px-1 pb-1">
              <div
                v-for="ev in (eventsByDate[date] || [])"
                :key="ev.id"
                :class="[ev.color, 'white--text', { 'calendar-chip--everyone': ev.everyone }]"
                class="mb-1 px-2 py-1 rounded text-caption text-truncate calendar-chip"
                :title="ev.title"
                @click.stop="openCampaign(ev.campaignId)"
              >
                <v-icon
                  v-if="ev.everyone"
                  x-small
                  color="white"
                  class="mr-1"
                >
                  mdi-account-group
                </v-icon>{{ ev.name }}
              </div>
            </div>
          </template>
        </v-calendar>
      </v-col>

      <v-col
        cols="12"
        md="3"
      >
        <div class="text-subtitle-2 mb-2">
          Unscheduled
        </div>
        <v-list
          v-if="unscheduled.length"
          dense
        >
          <v-list-item
            v-for="c in unscheduled"
            :key="c.id"
            @click="openCampaign(c.id)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ c.name }}</v-list-item-title>
            </v-list-item-content>
            <!-- Placing a campaign is the one thing this list is for, so it doesn't need a trip to the detail
                 page: the button runs the same auto-scheduler and the campaign moves onto the calendar. -->
            <v-list-item-action>
              <v-btn
                small
                text
                color="primary"
                :loading="schedulingId === c.id"
                @click.stop="setDate(c)"
              >
                Set date
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div
          v-else
          class="text-caption grey--text"
        >
          Nothing waiting to be scheduled.
        </div>
      </v-col>
    </v-row>
  </v-card-text>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  isEveryoneEmail,
  itemStatus,
  matchesAudience,
  matchesNetworkReach,
  platformName
} from '@/helpers/marketing';

export default {
  name: 'CampaignCalendar',
  props: {
    typeFilter: {
      type: Number,
      default: null
    },
    // Audience key from helpers/marketing audienceKey(); null for "all audiences".
    audienceFilter: {
      type: String,
      default: null
    },
    // Display name of the filtered audience, for the caption that explains what's on screen.
    audienceName: {
      type: String,
      default: ''
    },
    // See NETWORK_REACH in helpers/marketing; null for "any reach".
    networkFilter: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      type: 'month',
      focus: '',
      title: '',
      schedulingId: null
    };
  },
  computed: {
    ...mapGetters({ allCampaigns: 'campaigns/list', settings: 'campaigns/settings' }),
    // Which networks "all networks" means today — the enabled set from Marketing settings, loaded by the
    // parent screen. Empty until it arrives; matchesNetworkReach handles that.
    enabledPlatforms() {
      return this.settings.enabledPlatforms || [];
    },
    campaigns() {
      return this.allCampaigns.filter(
        (c) =>
          (!this.typeFilter || c.type === this.typeFilter) &&
          matchesAudience(c, this.audienceFilter) &&
          matchesNetworkReach(c, this.networkFilter, this.enabledPlatforms)
      );
    },
    // Map of 'YYYY-MM-DD' -> [{ id, name, color, campaignId }] for the month-view day slot. A campaign shows ONE
    // chip per day even when it has several items on that day (e.g. a social post fanned out to N networks).
    eventsByDate() {
      const map = {};
      (this.campaigns || []).forEach((c) => {
        const everyone = this.everyoneFor(c);
        this.scheduledDays(c).forEach((entry) => {
          if (!map[entry.day]) map[entry.day] = [];
          map[entry.day].push({
            id: `${c.id}-${entry.day}`,
            name: c.name,
            color: entry.color,
            campaignId: c.id,
            everyone,
            title: this.chipTitle(c, everyone)
          });
        });
      });
      return map;
    },
    // Week view uses the built-in time grid, so feed it events only in that mode — also deduped per campaign/day.
    calendarEvents() {
      if (this.type !== 'week') return [];
      const list = [];
      (this.campaigns || []).forEach((c) => {
        const everyone = this.everyoneFor(c);
        this.scheduledDays(c).forEach((entry) => {
          const start = new Date(entry.send);
          list.push({
            // The time-grid event has no slot to hang an icon off, so the "everyone" signal and the networks
            // ride in the label.
            name: this.weekLabel(c, everyone),
            start,
            end: start,
            color: entry.color,
            campaignId: c.id
          });
        });
      });
      return list;
    },
    unscheduled() {
      return (this.campaigns || []).filter(
        (c) => !(c.items || []).some((i) => i.scheduledSend)
      );
    }
  },
  mounted() {
    if (!this.allCampaigns.length) {
      this.$store.dispatch('campaigns/getList').catch((error) => this.logError(error));
    }
  },
  methods: {
    // Unfiltered, the ring flags email broadcasts — the send you least want to double-book. Filtered to one
    // audience, it flags the campaigns that are on screen because they go to EVERYONE rather than because they
    // target this audience (emails with no work_area filter, and social posts, which every follower sees), so an
    // audience's week never looks more tailored to it than it is.
    everyoneFor(c) {
      return this.audienceFilter ? !!c.reachesEveryone : isEveryoneEmail(c);
    },
    // The month chip is one truncated line, so the networks it can't spell out live in its tooltip.
    chipTitle(c, everyone) {
      const parts = [c.name];
      const networks = this.networkNames(c);
      if (networks) parts.push(networks);
      if (everyone) parts.push(this.everyoneLabel(c));
      return parts.join(' — ');
    },
    weekLabel(c, everyone) {
      const parts = [c.name];
      const networks = this.networkNames(c);
      if (networks) parts.push(networks);
      if (everyone) parts.push('everyone');
      return parts.join(' — ');
    },
    networkNames(c) {
      return (c.selectedPlatforms || []).map(platformName).filter((n) => !!n).join(', ');
    },
    everyoneLabel(c) {
      return c && c.type === this.$enums.MARKETING.CAMPAIGN_TYPE.SOCIAL ? 'posted to everyone' : 'emails everyone';
    },
    // Collapses a campaign's scheduled items to one entry per day, so a social post fanned out to several
    // networks (all sharing a send date) renders as a single calendar chip rather than one per network.
    scheduledDays(c) {
      const byDay = {};
      (c.items || []).forEach((i) => {
        if (!i.scheduledSend) return;
        const day = this.toDateStr(i.scheduledSend);
        if (!byDay[day]) byDay[day] = { day, send: i.scheduledSend, statuses: [] };
        byDay[day].statuses.push(i.status);
      });
      return Object.keys(byDay).map((day) => ({
        day,
        send: byDay[day].send,
        color: itemStatus(this.dayStatus(byDay[day].statuses)).color
      }));
    },
    // One chip stands in for every item scheduled that day, so it has to report the day as a whole: a failure is
    // the thing you need to see, and otherwise the least-advanced item wins — a social post isn't "approved" on
    // the calendar while one of its networks is still a draft awaiting review.
    dayStatus(statuses) {
      const FAILED = 4;
      if (statuses.some((s) => s === FAILED)) return FAILED;
      return statuses.reduce((lowest, s) => (s < lowest ? s : lowest), statuses[0]);
    },
    toDateStr(iso) {
      const d = new Date(iso);
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    eventColor(event) {
      return event.color;
    },
    openEvent({ event }) {
      if (event && event.campaignId) {
        this.openCampaign(event.campaignId);
      }
    },
    // The calendar is a place you keep your bearings in, so opening a campaign shouldn't cost you the
    // month you were looking at — the detail page gets its own window.
    openCampaign(id) {
      const href = this.$router.resolve(`/marketing/campaigns/${id}`).href;
      window.open(href, '_blank', 'noopener');
    },
    // Hands an undated campaign to the auto-scheduler (the same one audience-plan and filled campaigns get). The
    // store swaps the refreshed campaign into the list, so it lands on the calendar without a reload.
    setDate(c) {
      this.schedulingId = c.id;
      this.$store
        .dispatch('campaigns/scheduleCampaign', { id: c.id })
        .then(() => this.$root.$snackbarSuccess(`${c.name} scheduled.`))
        .catch((error) => this.logError(error))
        .finally(() => {
          this.schedulingId = null;
        });
    },
    updateTitle({ start, end }) {
      if (start && end) {
        this.title = `${start.month}/${start.year}`;
      }
    }
  }
};
</script>

<style scoped>
.calendar-chip {
  cursor: pointer;
  font-size: 11px;
  line-height: 1.4;
}
/* An email that goes to the whole list is the one you least want to double-book, so it gets a ring + shadow on
   top of its status color rather than a color of its own (status stays readable). */
.calendar-chip--everyone {
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.45);
  font-weight: 600;
  /* box-shadow doesn't reserve space — keep the ring off the neighbouring chip. */
  margin-top: 3px;
  margin-bottom: 7px !important;
}
/* The month grid renders its own chips (see the #day slot), so it has to size to its content. v-calendar's
   default is the opposite: a fixed-height container whose week rows flex to fill it and clip the overflow,
   which cuts the last week — and any day with more than a chip or two — off the bottom. These three rules
   turn that off: the grid grows, each week is its own height, and day cells don't clip.
   Weekly markup only, so the week view's time grid keeps its own scrolling layout. */
::v-deep .v-calendar-weekly {
  height: auto !important;
}
::v-deep .v-calendar-weekly__week {
  flex: none;
  min-height: 90px;
}
::v-deep .v-calendar-weekly__day {
  min-height: 90px;
  overflow: visible;
}
</style>
